import { Cloud, DollarSign, Info, Loader2, Server, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { formatCurrency } from "@/lib/formatters";
import { platformCostsService } from "@/lib/services";
import type { DailyCost, PlatformCostsResponse, ResourceGroupCost } from "@/types";

// ── Summary cards ────────────────────────────────────────────────────────────

interface CostSummaryCardsProps {
  totalCostUsd: number;
  dailyAvg: number;
  environmentCount: number;
  days: number;
}

function CostSummaryCards({ totalCostUsd, dailyAvg, environmentCount, days }: Readonly<CostSummaryCardsProps>) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-4 h-4 text-content-tertiary" />
          <p className="text-sm text-content-tertiary">Total Platform Cost</p>
        </div>
        <p className="text-2xl font-semibold text-content-primary">{formatCurrency(totalCostUsd)}</p>
        <p className="text-xs text-content-tertiary mt-1">Last {days} days</p>
      </div>

      <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-4 h-4 text-content-tertiary" />
          <p className="text-sm text-content-tertiary">Daily Average</p>
        </div>
        <p className="text-2xl font-semibold text-content-primary">{formatCurrency(dailyAvg)}</p>
        <p className="text-xs text-content-tertiary mt-1">~{formatCurrency(dailyAvg * 30)}/month projected</p>
      </div>

      <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <Server className="w-4 h-4 text-content-tertiary" />
          <p className="text-sm text-content-tertiary">Environments</p>
        </div>
        <p className="text-2xl font-semibold text-content-primary">{environmentCount}</p>
        <p className="text-xs text-content-tertiary mt-1">Active resource groups</p>
      </div>
    </div>
  );
}

// ── Daily trend sparkline ────────────────────────────────────────────────────

interface DailyTrendChartProps {
  dailyTrend: DailyCost[];
}

function DailyTrendChart({ dailyTrend }: Readonly<DailyTrendChartProps>) {
  if (dailyTrend.length === 0) return null;

  const maxDailyCost = Math.max(...dailyTrend.map((d) => d.costUsd), 1);

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <h4 className="text-sm font-medium text-content-primary mb-4">Daily Cost Trend</h4>
      <div className="flex">
        {/* Y-axis labels */}
        <div
          className="flex flex-col justify-between h-20 pr-2 text-xs text-content-tertiary text-right shrink-0"
          style={{ minWidth: "2.5rem" }}
        >
          <span>{formatCurrency(maxDailyCost)}</span>
          <span>{formatCurrency(maxDailyCost / 2)}</span>
          <span>$0</span>
        </div>
        {/* Chart area */}
        <div className="flex-1 min-w-0">
          <div className="flex items-end gap-[2px] h-20">
            {dailyTrend.map((day) => {
              const heightPct = Math.max((day.costUsd / maxDailyCost) * 100, 2);
              return (
                <div key={day.date} className="flex-1 relative group" style={{ height: `${heightPct}%` }}>
                  <div className="absolute inset-0 bg-brand-primary opacity-60 group-hover:opacity-100 rounded-t transition-opacity" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
                    <div className="bg-surface-inverse text-content-inverse text-xs px-2 py-1 rounded-sm whitespace-nowrap">
                      {day.date}: {formatCurrency(day.costUsd)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-content-tertiary mt-2">
            <span>{dailyTrend[0]?.date}</span>
            <span>{dailyTrend.at(-1)?.date}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Cost by environment ──────────────────────────────────────────────────────

interface CostByEnvironmentProps {
  resourceGroups: ResourceGroupCost[];
}

function CostByEnvironment({ resourceGroups }: Readonly<CostByEnvironmentProps>) {
  if (resourceGroups.length === 0) return null;

  const maxRgCost = Math.max(...resourceGroups.map((r) => r.costUsd), 1);

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <h4 className="text-sm font-medium text-content-primary mb-4">Cost by Environment</h4>
      <div className="space-y-3">
        {resourceGroups.map((rg) => {
          const percentage = (rg.costUsd / maxRgCost) * 100;
          return (
            <div key={rg.resourceGroup}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-content-primary">{friendlyRgName(rg.resourceGroup)}</span>
                <span className="text-content-primary font-medium">{formatCurrency(rg.costUsd)}</span>
              </div>
              <div className="h-2 bg-surface-secondary rounded-full overflow-hidden">
                <div className="h-full bg-brand-gradient rounded-full" style={{ width: `${percentage}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Cost by service ──────────────────────────────────────────────────────────

interface ServiceBreakdownEntry {
  name: string;
  cost: number;
}

interface CostByServiceProps {
  services: ServiceBreakdownEntry[];
}

function CostByService({ services }: Readonly<CostByServiceProps>) {
  if (services.length === 0) return null;

  const maxServiceCost = Math.max(...services.map((s) => s.cost), 1);

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <h4 className="text-sm font-medium text-content-primary mb-4">Cost by Service</h4>
      <div className="space-y-3">
        {services.map((svc) => {
          const percentage = (svc.cost / maxServiceCost) * 100;
          return (
            <div key={svc.name}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-content-primary">{svc.name}</span>
                <span className="text-content-primary font-medium">{formatCurrency(svc.cost)}</span>
              </div>
              <div className="h-2 bg-surface-secondary rounded-full overflow-hidden">
                <div className="h-full bg-mutual-purple/60 rounded-full" style={{ width: `${percentage}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Helper functions ─────────────────────────────────────────────────────────

function friendlyRgName(rg: string) {
  // rg-anthm-dev -> Dev, rg-anthm-prod -> Prod, rg-anthm-shared -> Shared, etc.
  // Also handles legacy rg-mutual-* names in historical cost data.
  const suffix = rg.replace(/^rg-(mutual|anthm)-/, "");
  return suffix.charAt(0).toUpperCase() + suffix.slice(1);
}

function friendlyServiceName(name: string) {
  // Clean up Azure service names for display
  return name
    .replace("Azure ", "")
    .replace("Foundry Models", "AI Models (OpenAI)")
    .replace("Foundry Tools", "AI Tools (Search / Embeddings)")
    .replace(/^SaaS$/, "SaaS (Partner Models — Anthropic, etc.)");
}

// ── Main component ───────────────────────────────────────────────────────────

export function PlatformCosts() {
  const [costs, setCosts] = useState<PlatformCostsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<30 | 7 | 90>(30);

  useEffect(() => {
    loadCosts();
  }, [days]);

  const loadCosts = async () => {
    try {
      setLoading(true);
      const response = await platformCostsService.getOverview(days);
      setCosts(response.data);
    } catch (err) {
      console.error("Failed to load platform costs:", err);
      // Don't toast – this is a supplemental section; silent fail is fine
    } finally {
      setLoading(false);
    }
  };

  // Don't show anything while still loading for the first time
  if (loading && !costs) {
    return (
      <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Cloud className="w-5 h-5 text-content-tertiary" />
          <h3 className="font-medium text-content-primary">Azure Platform Costs</h3>
        </div>
        <div className="flex flex-col items-center justify-center h-32 gap-3">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
          <p className="text-xs text-content-tertiary">
            Loading billing data. This can take 2–3 minutes due to API rate limits.
          </p>
        </div>
      </div>
    );
  }

  // Not configured or failed
  if (!costs?.configured) {
    return (
      <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <Cloud className="w-5 h-5 text-content-tertiary" />
          <h3 className="font-medium text-content-primary">Azure Platform Costs</h3>
        </div>
        <div className="flex items-center gap-3 p-4 bg-surface-secondary rounded-lg">
          <Info className="w-5 h-5 text-content-tertiary shrink-0" />
          <p className="text-sm text-content-secondary">
            {costs?.message ||
              "Azure cost data is not available. Ensure the service principal has the 'Cost Management Reader' role."}
          </p>
        </div>
      </div>
    );
  }

  // Build aggregated service breakdown (across all RGs)
  const serviceAgg: Record<string, number> = {};
  for (const entry of costs.byService) {
    const name = friendlyServiceName(entry.serviceName);
    serviceAgg[name] = (serviceAgg[name] || 0) + entry.costUsd;
  }
  const serviceBreakdown = Object.entries(serviceAgg)
    .map(([name, cost]) => ({ name, cost: Math.round(cost * 100) / 100 }))
    .filter((s) => s.cost > 0)
    .sort((a, b) => b.cost - a.cost);

  // Compute daily average from trend
  const dailyAvg =
    costs.dailyTrend.length > 0 ? costs.dailyTrend.reduce((sum, d) => sum + d.costUsd, 0) / costs.dailyTrend.length : 0;

  return (
    <div className="space-y-6">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Cloud className="w-5 h-5 text-content-tertiary" />
          <h3 className="font-medium text-content-primary">Azure Platform Costs</h3>
          <span className="text-xs text-content-tertiary bg-surface-secondary px-2 py-0.5 rounded-full">
            Actual billing data
          </span>
          {loading && <Loader2 className="w-4 h-4 text-brand-primary animate-spin" />}
        </div>
        <label className="flex items-center gap-2 text-xs text-content-tertiary">
          <span>Azure Costs range:</span>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value) as 7 | 30 | 90)}
            aria-label="Azure Platform Costs date range"
            className="px-3 py-1.5 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </label>
      </div>

      <CostSummaryCards
        totalCostUsd={costs.totalCostUsd}
        dailyAvg={dailyAvg}
        environmentCount={costs.byResourceGroup.length}
        days={days}
      />

      <DailyTrendChart dailyTrend={costs.dailyTrend} />

      <CostByEnvironment resourceGroups={costs.byResourceGroup} />

      <CostByService services={serviceBreakdown} />

      {/* Data lag notice */}
      <div className="flex items-center gap-2 text-xs text-content-tertiary">
        <Info className="w-3.5 h-3.5 shrink-0" />
        <span>Azure billing data may lag 24-48 hours. Costs are cached for 1 hour.</span>
      </div>
    </div>
  );
}
